import React from "react"

import { ExternalDimensioningConfigurationMismatchError } from "shared/types/ExternalDimensioning"
import { ProcessStep } from "shared/types/ProcessStep"
import {
  ProductConfigurationFormData,
  ProductConfiguratorStepType
} from "shared/types/ProductConfiguration"
import { ProductConfiguratorAddonOption } from "shared/types/ProductConfigurator"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import { RJSFSchema } from "shared/types/RJSF"
import { WithClassName } from "shared/types/WithClassName"
import {
  DimensioningOption,
  ExternalDimensioningResult,
  ProductConfigurationResponse,
  ProductInformation
} from "shared/types/__generated__/graphql"

import {
  AttributeInformationContext,
  AttributeInformationContextType
} from "./model/AttributeInformationContext"
import { ProductConfiguratorConfigurationContextProvider } from "./model/ProductConfiguratorConfigurationContext"
import { ProductConfiguratorProductInfoContextProvider } from "./model/ProductConfiguratorProductInfoContext"
import { ProductConfiguratorStatusContextProvider } from "./model/ProductConfiguratorStatusContext"
import { ProductConfiguratorStepsFallbackProvider } from "./model/ProductConfiguratorStepsFallbackProvider"
import { useProductConfiguratorViewModel } from "./model/useProductConfiguratorViewModel"
import { ProductConfiguratorView } from "./ui/ProductConfiguratorView"
import {
  ExternalDimensioningBaseProductCardComponentProps,
  ExternalProductDimensioningModel
} from "./ui/external-dimensioning/ExternalProductDimensioning"

export type ProductConfiguratorStep = ProcessStep<ProductConfiguratorStepType>

export interface ProductConfiguratorModel {
  schema: RJSFSchema | null | undefined
  formData: ProductConfigurationFormData | null | undefined

  productConfigurationResponse: ProductConfigurationResponse | null | undefined

  onFormDataChange: (
    formData: ProductConfigurationFormData,
    fieldID?: string
  ) => void | Promise<unknown>

  isConfigurationServiceAvailable?: boolean
  isConfigurable?: boolean
  isConfigurationModelFaulty?: boolean

  baseVariantSelection?: React.ReactNode | null

  addonOptions?: ProductConfiguratorAddonOption[] | null
  onAddonDelete?: (addonKey: string) => void

  isReady?: boolean

  isLoading?: boolean
  isUpdating?: boolean
  isDisabled?: boolean
  isSubmitting?: boolean

  isConfigurationWip?: boolean

  hasError?: boolean
  errorMessages?: string[] | null

  selectedDimensioningID?: string | null | undefined
  dimensioningResults?: DimensioningOption[] | null
  onDimensioningSelect: (dimensioningID: string) => void

  variantKey: string
  onVariantKeyChange?: (variantKey: string) => void

  externalProductDimensioningModel?: ExternalProductDimensioningModel
  externalDimensioningBaseProductCardComponent?: React.ComponentType<ExternalDimensioningBaseProductCardComponentProps>
  onSkipDimensioningStep?: () => void

  externalDimensioningConfigurationMismatchError?:
    | ExternalDimensioningConfigurationMismatchError
    | null
    | undefined

  productTitle?: string
  productKind?: string
  productImage?: React.ReactNode

  productInformation?: ProductInformation | null | undefined
  isLoadingProductInformation?: boolean

  onSubmit?: () => void

  apiRef?: React.RefObject<ProductConfiguratorAPI | null>

  attributeInformationContext?: AttributeInformationContextType

  mode?: ProductConfiguratorMode
}

export interface ProductConfiguratorAPI {
  onCompleteExternalProductDimensioning: (
    externalProductDimensioning: ExternalDimensioningResult
  ) => void
}

export interface ProductConfiguratorProps
  extends ProductConfiguratorModel,
    WithClassName {
  hideTitle?: boolean
  isFullWidth?: boolean
  scrollOnTabChange?: boolean

  initialStep?: ProductConfiguratorStepType

  hideFallbackBackupVariantKeyInput?: boolean
}

function ProductConfiguratorContent(model: ProductConfiguratorProps) {
  return <ProductConfiguratorView {...useProductConfiguratorViewModel(model)} />
}

export const ProductConfigurator = React.memo(
  (model: ProductConfiguratorProps) => {
    return (
      <AttributeInformationContext.Provider
        value={model.attributeInformationContext}
      >
        <ProductConfiguratorStepsFallbackProvider model={model}>
          <ProductConfiguratorConfigurationContextProvider model={model}>
            <ProductConfiguratorStatusContextProvider model={model}>
              <ProductConfiguratorProductInfoContextProvider model={model}>
                <ProductConfiguratorContent {...model} />
              </ProductConfiguratorProductInfoContextProvider>
            </ProductConfiguratorStatusContextProvider>
          </ProductConfiguratorConfigurationContextProvider>
        </ProductConfiguratorStepsFallbackProvider>
      </AttributeInformationContext.Provider>
    )
  }
)
